<script setup lang="ts">
  import BfCounter from './BfCounter.vue'

  interface BfTab {
    label: string
    /** Value emitted on select; defaults to `label` when omitted. */
    value?: string
    /** Optional count badge shown after the label. */
    count?: number
  }

  withDefaults(
    defineProps<{
      tabs: BfTab[]
      modelValue?: string
      ariaLabel?: string
    }>(),
    { modelValue: '', ariaLabel: 'Tabs' },
  )

  defineEmits<{ 'update:modelValue': [value: string] }>()

  const resolveValue = (tab: BfTab): string => tab.value ?? tab.label
</script>

<template>
  <div class="tabs tabs--boxed" role="group" :aria-label="ariaLabel">
    <button
      v-for="tab in tabs"
      :key="resolveValue(tab)"
      type="button"
      class="tabs__tab"
      :aria-pressed="modelValue === resolveValue(tab)"
      @click="$emit('update:modelValue', resolveValue(tab))"
    >
      {{ tab.label }}
      <BfCounter v-if="tab.count !== undefined" class="tabs__count">{{ tab.count }}</BfCounter>
    </button>
  </div>
</template>

<style scoped lang="scss">
  .tabs--boxed {
    display: inline-flex;
    flex-wrap: wrap;
    width: fit-content;
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-md);
    overflow: hidden;
    background: var(--bf-white);
  }

  .tabs__tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 35px;
    padding: 0 18px;
    box-sizing: border-box;
    font-family: var(--bf-font-display);
    font-size: 11px;
    font-weight: var(--bf-fw-semibold);
    line-height: 1;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--bf-dusky-1);
    background: var(--bf-white);
    border: none;
    border-right: 1px solid var(--bf-line);
    cursor: pointer;
    transition: all var(--bf-dur-base) var(--bf-ease);
  }

  .tabs__tab:last-child {
    border-right: none;
  }

  .tabs__tab:hover {
    color: var(--bf-orange-dark);
    background: var(--bf-nav-active);
  }

  .tabs__tab[aria-pressed='true'] {
    color: var(--bf-orange-dark);
    background: var(--bf-nav-active);
    box-shadow: inset 0 -2px 0 0 var(--bf-orange-dark);
  }

  .tabs__tab:focus-visible {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: -2px;
  }

  /* Positional hook only: the pill is BfCounter, mixed in as .tabs__count.counter. */
  .tabs--boxed .tabs__count {
    margin-left: 6px;
  }

  /* Selected state belongs to the container: it overrides the counter's two colour
     properties, so aria-pressed is the only thing that changes. */
  .tabs--boxed .tabs__tab[aria-pressed='true'] .counter {
    --counter-surface: var(--bf-orange-ultralight);
    --counter-text: var(--bf-orange-dark);
  }

  @media (max-width: 700px) {
    .tabs--boxed {
      display: flex;
      width: 100%;
      flex-wrap: nowrap;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .tabs__tab {
      flex: 0 0 auto;
      white-space: nowrap;
    }
  }
</style>
